import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useCallback } from 'react';
import { artifactExportService, ExportFormat } from '../services/artifactExportService';
import { demoProjectsService } from '../services/demoProjectsService';
import { useProjectData } from './ProjectDataContext';
import { useUserProjectsContext } from './UserProjectsContext';
import { isContourName, isContourBlockedAtLowRes, parseContourInterval } from '../utils/contourNames';
import { isLiveDemo, isScouting } from '../utils/capabilities';
import { artifactLayerFromMetadataJson, createLayerOpenDeduper } from '../utils/layerOpened';
import { projectService } from '../services/projectService';
import { eventBus } from '../events';
const ArtifactActionsContext = createContext(undefined);
// Page memory for RecordLayerOpened: a (project, object key) is reported once
// per page session however often it is toggled. The server sums whatever arrives.
const firstLayerOpenThisSession = createLayerOpenDeduper();
export const ArtifactActionsProvider = ({ children, onLoadArtifact, onToggleArtifact, onUpdateArtifactOpacity, onUpdateArtifactWeight, onUpdateArtifactMonochrome, onHideAllArtifacts, onAddStatus }) => {
    const { selectedProject, artifacts } = useProjectData();
    const { projects: userProjects } = useUserProjectsContext();
    const [exportState, setExportState] = useState({
        isExporting: new Map(),
        lastExportResults: new Map()
    });
    // The selected project when it is served the low-res scouting set, else null.
    // accessLevel is a fetch-time snapshot, so a live demo that expired since the
    // last fetch also counts as low-res.
    const selectedLowResProject = useCallback(() => {
        var _a;
        const projectId = (_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject);
        const up = projectId ? userProjects.find(p => p.id === projectId) : undefined;
        if (!up)
            return null;
        const expiredSinceFetch = isLiveDemo(up.accessLevel) && up.demoExpiresAt != null && Date.now() / 1000 >= up.demoExpiresAt;
        const isLowRes = isScouting(up.accessLevel) || expiredSinceFetch;
        return isLowRes ? up : null;
    }, [selectedProject, userProjects]);
    // Only the coarsest contour intervals are meaningful on the scouting set, so
    // showing a finer one opens the data-quality explainer instead. Returns true
    // when gated. Both show paths (first load and re-show) call it.
    const maybeGateContour = useCallback((name) => {
        if (!name || !isContourName(name) || !isContourBlockedAtLowRes(name))
            return false;
        const up = selectedLowResProject();
        if (!up)
            return false;
        const parsed = parseContourInterval(name);
        eventBus.emit('contours:lowResGated', {
            intervalLabel: parsed ? `${parsed.value} ${parsed.unit}` : name,
            projectId: up.id,
            projectName: up.name,
            demoExpiresAt: up.demoExpiresAt,
        });
        return true;
    }, [selectedLowResProject]);
    // Exports on the scouting set need a paid unlock, so they open the unlock
    // modal. Preview, unbuilt and restoring projects already have full access but
    // no full-resolution set yet: they get a notice, never the payment modal.
    const maybeGateExport = useCallback(() => {
        var _a;
        const projectId = (_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject);
        const owned = projectId ? userProjects.find(p => p.id === projectId) : undefined;
        // Offering a build here would name an action the server is already performing.
        if (owned && owned.displayStatus === 'restoring_preview') {
            onAddStatus('This project is restoring its preview. Exports will be available once it is rebuilt at full resolution.');
            return true;
        }
        if (owned && (owned.displayStatus === 'preview' || owned.displayStatus === 'unbuilt')) {
            onAddStatus('This project is at preview resolution. Build it at full resolution first, included while your Atlas plan is active.');
            return true;
        }
        const up = selectedLowResProject();
        if (!up)
            return false;
        eventBus.emit('project:openUnlockModal', {
            projectId: up.id,
            projectName: up.name,
            demoExpiresAt: up.demoExpiresAt,
        });
        return true;
    }, [selectedProject, userProjects, onAddStatus, selectedLowResProject]);
    // Counts a layer the user switched on in the layer tree. Scope restores and the
    // tool panels' pickers load elsewhere and are not counted. Only projects in the
    // caller's own list report: the legacy demo showcase has no such RPC.
    const reportLayerOpened = useCallback((metadata) => {
        var _a, _b;
        const projectId = (_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject);
        if (!projectId || !userProjects.some(p => p.id === projectId))
            return;
        const { objectKey, layer } = artifactLayerFromMetadataJson((_b = metadata === null || metadata === void 0 ? void 0 : metadata.getMetadata) === null || _b === void 0 ? void 0 : _b.call(metadata));
        if (!objectKey || !layer || !firstLayerOpenThisSession(projectId, layer))
            return;
        projectService.recordLayerOpened(projectId, objectKey).catch((err) => {
            console.warn('RecordLayerOpened failed', err);
        });
    }, [selectedProject, userProjects]);
    const handleLoadArtifact = useCallback((artifactId, metadata) => {
        var _a, _b;
        const name = (_b = (_a = metadata === null || metadata === void 0 ? void 0 : metadata.getName) === null || _a === void 0 ? void 0 : _a.call(metadata)) !== null && _b !== void 0 ? _b : '';
        if (maybeGateContour(name))
            return;
        reportLayerOpened(metadata);
        onLoadArtifact(artifactId, metadata);
    }, [onLoadArtifact, maybeGateContour, reportLayerOpened]);
    const handleToggleArtifact = useCallback((artifactId, visible) => {
        var _a, _b;
        if (visible) {
            const metadata = artifacts.find(a => { var _a; return ((_a = a.getId) === null || _a === void 0 ? void 0 : _a.call(a)) === artifactId; });
            const name = (_b = (_a = metadata === null || metadata === void 0 ? void 0 : metadata.getName) === null || _a === void 0 ? void 0 : _a.call(metadata)) !== null && _b !== void 0 ? _b : '';
            if (maybeGateContour(name))
                return;
            reportLayerOpened(metadata);
        }
        onToggleArtifact(artifactId, visible);
    }, [onToggleArtifact, artifacts, maybeGateContour, reportLayerOpened]);
    const onExportArtifact = useCallback(async (artifactId, format, isDemoProject = false) => {
        if (maybeGateExport())
            return;
        try {
            setExportState(prev => ({
                ...prev,
                isExporting: new Map(prev.isExporting).set(artifactId, true)
            }));
            onAddStatus(`🔄 Exporting artifact to ${artifactExportService.getFormatName(format)}...`);
            const result = await artifactExportService.exportArtifact(artifactId, format, {}, isDemoProject);
            setExportState(prev => ({
                ...prev,
                isExporting: new Map(prev.isExporting).set(artifactId, false),
                lastExportResults: new Map(prev.lastExportResults).set(artifactId, result)
            }));
            if (result.success && result.downloadUrl) {
                onAddStatus(`✅ Export successful! Download starting...`);
                artifactExportService.initiateDownload(result);
            }
            else {
                onAddStatus(`❌ Export failed: ${result.message}`);
            }
        }
        catch (error) {
            setExportState(prev => ({
                ...prev,
                isExporting: new Map(prev.isExporting).set(artifactId, false)
            }));
            // UNAUTHENTICATED (16) and PERMISSION_DENIED (7) are handled by the context menu.
            if (error && typeof error === 'object' && 'code' in error &&
                (error.code === 16 || error.code === 7)) {
                throw error;
            }
            const errorMessage = error instanceof Error ? error.message : 'Unknown export error';
            onAddStatus(`❌ Export failed: ${errorMessage}`);
            console.error('Export error:', error);
        }
    }, [onAddStatus, maybeGateExport]);
    const onGetSupportedFormats = useCallback(async (artifactId, isDemoProject, artifactMetadata) => {
        var _a;
        try {
            // The backend needs projectId and layerName, not the artifact database ID.
            if (!artifactMetadata) {
                console.error(`Artifact metadata is required for getSupportedFormats`);
                return [];
            }
            const projectId = (_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject);
            if (!projectId) {
                console.error(`No project currently selected - cannot determine project ID for getSupportedFormats`);
                return [];
            }
            let layerName = '';
            try {
                const metadataJson = artifactMetadata.getMetadata();
                const metadata = JSON.parse(metadataJson || '{}');
                const objectKey = metadata.object_key;
                if (objectKey) {
                    const lastSlashIndex = objectKey.lastIndexOf('/');
                    if (lastSlashIndex >= 0) {
                        const filename = objectKey.substring(lastSlashIndex + 1);
                        const dotIndex = filename.lastIndexOf('.');
                        layerName = dotIndex > 0 ? filename.substring(0, dotIndex) : filename;
                        // Strip _display suffix if present (matches backend logic)
                        if (layerName.endsWith('_display')) {
                            layerName = layerName.substring(0, layerName.length - '_display'.length);
                        }
                    }
                }
            }
            catch (e) {
                console.warn('Failed to parse artifact metadata JSON:', e);
            }
            // Fallback to using artifact name if object key extraction failed
            if (!layerName) {
                const artifactName = artifactMetadata.getName();
                const dotIndex = artifactName.lastIndexOf('.');
                layerName = dotIndex > 0 ? artifactName.substring(0, dotIndex) : artifactName;
            }
            let result;
            if (isDemoProject) {
                const response = await demoProjectsService.getSupportedExportFormats(projectId, layerName);
                const availableFormats = response.getAvailableFormatsList();
                result = {
                    success: true,
                    availableFormats: availableFormats.map((af) => ({
                        format: af.getFormat(),
                        artifactId: af.getArtifactId()
                    }))
                };
            }
            else {
                result = await artifactExportService.getSupportedExportFormats(projectId, layerName);
            }
            const filteredFormats = result.availableFormats.filter((af) => af.format !== ExportFormat.EXPORT_FORMAT_UNSPECIFIED);
            return filteredFormats;
        }
        catch (error) {
            let extractedProjectId = 'unknown';
            let extractedLayerName = 'unknown';
            try {
                const folderId = artifactMetadata === null || artifactMetadata === void 0 ? void 0 : artifactMetadata.getFolderId();
                if (folderId) {
                    extractedProjectId = folderId.split(':')[0] || 'unknown';
                }
                const metadataJson = artifactMetadata === null || artifactMetadata === void 0 ? void 0 : artifactMetadata.getMetadata();
                if (metadataJson) {
                    const metadata = JSON.parse(metadataJson || '{}');
                    const objectKey = metadata.object_key;
                    if (objectKey) {
                        const lastSlashIndex = objectKey.lastIndexOf('/');
                        if (lastSlashIndex >= 0) {
                            const filename = objectKey.substring(lastSlashIndex + 1);
                            const dotIndex = filename.lastIndexOf('.');
                            extractedLayerName = dotIndex > 0 ? filename.substring(0, dotIndex) : filename;
                        }
                    }
                }
            }
            catch (e) {
                // Ignore extraction errors in error handler
            }
            console.error('Frontend Error getting supported formats:', {
                artifactId,
                projectId: extractedProjectId,
                layerName: extractedLayerName,
                isDemoProject,
                error: error instanceof Error ? error.message : error,
                stack: error instanceof Error ? error.stack : undefined
            });
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            onAddStatus(`Failed to get export formats: ${errorMsg}`);
            return [];
        }
    }, [selectedProject, onAddStatus]);
    const isArtifactExporting = useCallback((artifactId) => {
        return exportState.isExporting.get(artifactId) || false;
    }, [exportState.isExporting]);
    const getLastExportResult = useCallback((artifactId) => {
        return exportState.lastExportResults.get(artifactId);
    }, [exportState.lastExportResults]);
    const value = {
        onLoadArtifact: handleLoadArtifact,
        onToggleArtifact: handleToggleArtifact,
        onUpdateArtifactOpacity,
        onUpdateArtifactWeight,
        onUpdateArtifactMonochrome,
        onHideAllArtifacts,
        onExportArtifact,
        onGetSupportedFormats,
        isArtifactExporting,
        getLastExportResult
    };
    return (_jsx(ArtifactActionsContext.Provider, { value: value, children: children }));
};
export const useArtifactActions = () => {
    const context = useContext(ArtifactActionsContext);
    if (context === undefined) {
        throw new Error('useArtifactActions must be used within an ArtifactActionsProvider');
    }
    return context;
};
